import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { LOYALTY_TIER_LABELS, LOYALTY_TIER_THRESHOLDS } from '@golf-booking/shared';

const tierColors: Record<string, string> = {
  bronze: 'from-amber-600 to-amber-800',
  silver: 'from-gray-400 to-gray-600',
  gold: 'from-yellow-500 to-yellow-700',
  platinum: 'from-slate-400 to-slate-600',
};

export function LoyaltyPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'rewards' | 'history'>('overview');

  const { data: account, isLoading: accountLoading } = useQuery({
    queryKey: ['loyalty', 'account'],
    queryFn: () => api.loyalty.account(),
  });

  const { data: rewards, isLoading: rewardsLoading } = useQuery({
    queryKey: ['loyalty', 'rewards'],
    queryFn: () => api.loyalty.rewards(),
  });

  const { data: transactions, isLoading: transactionsLoading } = useQuery({
    queryKey: ['loyalty', 'transactions'],
    queryFn: () => api.loyalty.transactions({ pageSize: 20 }),
    enabled: activeTab === 'history',
  });

  if (accountLoading) {
    return (
      <div className="container-wide py-8">
        <Skeleton className="h-48 rounded-2xl mb-8" />
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  const accountData = account?.data;
  const tier = accountData?.tier || 'bronze';
  const nextTier = tier === 'bronze' ? 'silver' : tier === 'silver' ? 'gold' : tier === 'gold' ? 'platinum' : null;
  const pointsToNext = nextTier ? LOYALTY_TIER_THRESHOLDS[nextTier] - (accountData?.lifetimePoints || 0) : 0;
  const progressPercent = nextTier
    ? ((accountData?.lifetimePoints || 0) / LOYALTY_TIER_THRESHOLDS[nextTier]) * 100
    : 100;

  return (
    <div className="bg-sand-50 min-h-screen py-8">
      <div className="container-wide">
        {/* Header Card */}
        <Card className={`bg-gradient-to-br ${tierColors[tier]} text-white mb-8 overflow-hidden relative`}>
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

          <div className="relative">
            <div className="flex items-start justify-between mb-8">
              <div>
                <p className="text-white/80 text-sm mb-1">Your Status</p>
                <h1 className="text-3xl font-bold">{LOYALTY_TIER_LABELS[tier]} Member</h1>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-sm mb-1">Available Points</p>
                <p className="text-4xl font-bold">{accountData?.pointsBalance?.toLocaleString() || 0}</p>
              </div>
            </div>

            {nextTier && (
              <div>
                <div className="flex justify-between text-sm text-white/80 mb-2">
                  <span>{pointsToNext.toLocaleString()} points to {LOYALTY_TIER_LABELS[nextTier]}</span>
                  <span>{accountData?.lifetimePoints?.toLocaleString() || 0} / {LOYALTY_TIER_THRESHOLDS[nextTier].toLocaleString()}</span>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-white rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(progressPercent, 100)}%` }}
                  />
                </div>
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-white/20 flex gap-4">
              <div>
                <p className="text-white/80 text-sm">Lifetime Points</p>
                <p className="text-xl font-semibold">{accountData?.lifetimePoints?.toLocaleString() || 0}</p>
              </div>
              <div>
                <p className="text-white/80 text-sm">Referral Code</p>
                <p className="text-xl font-semibold">{accountData?.referralCode || '-'}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto no-scrollbar">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'rewards', label: 'Rewards' },
            { id: 'history', label: 'History' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-6 py-3 text-sm font-medium rounded-xl whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-sand-900 text-white'
                  : 'bg-white text-sand-700 hover:bg-sand-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Tier Benefits */}
            <Card>
              <h2 className="text-lg font-semibold text-sand-900 mb-4">Your Benefits</h2>
              <ul className="space-y-3">
                {[
                  { tier: 'bronze', benefit: 'Earn 10 points per $1 spent' },
                  { tier: 'silver', benefit: 'Early booking access (1 day)', minTier: 'silver' },
                  { tier: 'silver', benefit: '5% member discount', minTier: 'silver' },
                  { tier: 'gold', benefit: 'Early booking access (3 days)', minTier: 'gold' },
                  { tier: 'gold', benefit: '10% member discount', minTier: 'gold' },
                  { tier: 'platinum', benefit: 'Early booking access (7 days)', minTier: 'platinum' },
                  { tier: 'platinum', benefit: '15% member discount', minTier: 'platinum' },
                ].map((item, index) => {
                  const tiers = ['bronze', 'silver', 'gold', 'platinum'];
                  const userTierIndex = tiers.indexOf(tier);
                  const benefitTierIndex = item.minTier ? tiers.indexOf(item.minTier) : 0;
                  const isUnlocked = userTierIndex >= benefitTierIndex;

                  return (
                    <li key={index} className={`flex items-center gap-3 ${!isUnlocked ? 'opacity-40' : ''}`}>
                      <svg className={`h-5 w-5 ${isUnlocked ? 'text-sage-500' : 'text-sand-300'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isUnlocked ? 'M5 13l4 4L19 7' : 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'} />
                      </svg>
                      <span className="text-sm text-sand-700">{item.benefit}</span>
                    </li>
                  );
                })}
              </ul>
            </Card>

            {/* Earning Guide */}
            <Card>
              <h2 className="text-lg font-semibold text-sand-900 mb-4">How to Earn</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sage-100 text-sage-600 flex-shrink-0">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-sand-900">Book Tee Times</p>
                    <p className="text-sm text-sand-500">10 points per $1 spent</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sage-100 text-sage-600 flex-shrink-0">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-sand-900">Shop Pro Shop</p>
                    <p className="text-sm text-sand-500">10 points per $1 spent</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sage-100 text-sage-600 flex-shrink-0">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-sand-900">Refer Friends</p>
                    <p className="text-sm text-sand-500">1,000 bonus points each</p>
                  </div>
                </li>
              </ul>
            </Card>

            {/* Quick Actions */}
            <Card>
              <h2 className="text-lg font-semibold text-sand-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <Button variant="sage" className="w-full">
                  Browse Rewards
                </Button>
                <Button variant="outline" className="w-full">
                  Share Referral Code
                </Button>
              </div>
            </Card>
          </div>
        )}

        {activeTab === 'rewards' && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rewardsLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <Skeleton className="h-32" />
                </Card>
              ))
            ) : rewards?.data?.length === 0 ? (
              <Card className="sm:col-span-2 lg:col-span-3 text-center py-12">
                <p className="text-sand-500">No rewards available at this time.</p>
              </Card>
            ) : (
              rewards?.data?.map((reward) => {
                const canRedeem = (accountData?.pointsBalance || 0) >= reward.pointsCost;

                return (
                  <Card key={reward.id} variant="hover">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-sand-900">{reward.name}</h3>
                        <p className="text-sm text-sand-500 mt-1">{reward.description}</p>
                      </div>
                      <Badge variant="sage">{reward.minTier}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-lg font-bold text-sage-600">
                        {reward.pointsCost.toLocaleString()} pts
                      </p>
                      <Button
                        size="sm"
                        variant={canRedeem ? 'sage' : 'outline'}
                        disabled={!canRedeem}
                      >
                        {canRedeem ? 'Redeem' : 'Not enough points'}
                      </Button>
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <Card>
            {transactionsLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-16" />
                ))}
              </div>
            ) : transactions?.data?.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sand-500">No transactions yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-sand-100">
                {transactions?.data?.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-4">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-full ${
                        tx.type === 'earned' ? 'bg-sage-100 text-sage-600' :
                        tx.type === 'redeemed' ? 'bg-terracotta-100 text-terracotta-600' :
                        'bg-sand-100 text-sand-600'
                      }`}>
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={
                            tx.type === 'earned' ? 'M12 4v16m8-8H4' :
                            tx.type === 'redeemed' ? 'M20 12H4' :
                            'M13 17h8m0 0V9m0 8l-8-8-4 4-6-6'
                          } />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-sand-900">{tx.description}</p>
                        <p className="text-sm text-sand-500">{formatDate(tx.createdAt)}</p>
                      </div>
                    </div>
                    <p className={`text-lg font-semibold ${
                      tx.type === 'earned' ? 'text-sage-600' :
                      tx.type === 'redeemed' ? 'text-terracotta-600' :
                      'text-sand-600'
                    }`}>
                      {tx.type === 'earned' ? '+' : ''}{tx.points.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
